'use client';

import { Stack, Typography, Box, Grid } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import logo from '@/assets/images/logo.webp';
import theme from '@/theme/theme';

import notionGreyIcon from '@/assets/images/icons/notionGrayIcon.webp';
import linkedinGreyIcon from '@/assets/images/icons/linkedinGrayIcon.webp';
import githubGreyIcon from '@/assets/images/icons/githubGrayIcon.webp';
import xGreyIcon from '@/assets/images/icons/XGrayIcon.webp';
import instagramGreyIcon from '@/assets/images/icons/instagramGrayIcon.webp';
import notionIcon from '@/assets/images/icons/notionIcon.webp';
import linkedinIcon from '@/assets/images/icons/linkedinIcon.webp';
import githubIcon from '@/assets/images/icons/githubIcon.webp';
import xIcon from '@/assets/images/icons/XIcon.webp';
import instagramIcon from '@/assets/images/icons/instagramIcon.webp';

const SOCIAL_LINKS = [
    { id: 'notion', href: 'https://moodglobalservices.notion.site/Portfolio-Mood-Global-Services-203bce8c178b4b6aa6cabeb6309d18f8', grey: notionGreyIcon, color: notionIcon },
    { id: 'linkedin', href: 'https://www.linkedin.com/company/moods-dev-studio', grey: linkedinGreyIcon, color: linkedinIcon },
    { id: 'github', href: 'https://github.com/Mood-Global-Services', grey: githubGreyIcon, color: githubIcon },
    { id: 'x', href: 'https://x.com/MoodServices', grey: xGreyIcon, color: xIcon },
    { id: 'instagram', href: 'https://www.instagram.com/moodsbuild/', grey: instagramGreyIcon, color: instagramIcon },
];

const NAV_LINKS = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Quantum Readiness', path: '/quantum-readiness' },
    { label: 'SiteLab', path: 'https://www.sitelab.build/' },
    { label: 'BlockAI', path: '/blockAI', isSoon: true },
    { label: 'About Us', path: '/about' },
    { label: 'Portal', path: '/portal' },
];

const LEGAL_LINKS = [
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Terms and Conditions', path: '/terms-conditions' },
    { label: 'Cookie Policy', path: '/cookie-policy' },
];

const CONTACT_DATA = {
    address: { label: 'Spinozastraat 47C, 1018 HJ\nAmsterdam, Netherlands', href: 'https://www.google.com/maps/place/Spinozastraat+47C,+1018+HJ+Amsterdam,+Paesi+Bassi/@52.3613262,4.8925315,15z/data=!3m1!4b1!4m6!3m5!1s0x47c609996552b159:0x5d6e292e8c6c79bf!8m2!3d52.3613145!4d4.9109854!16s%2Fg%2F11vrtp2rs3?entry=ttu&g_ep=EgoyMDI2MDMxMS4wIKXMDSoASAFQAw%3D%3D' },
    email: { label: 'info@moodglobalservices.com', href: 'mailto:info@moodglobalservices.com' },
    kvk: { label: 'KVK: 85502081', href: 'https://www.kvk.nl/zoeken/?zoekterm=85502081' },
    tel: { label: '+31-6-19565119', href: 'tel:+31619565119' },
};

export default function Footer() {
    return (
        <Stack
            component="footer"
            width="100%"
            maxWidth="100%"
            sx={{ color: 'text.primary', pt: { xs: 6, md: 10 }, pb: { xs: 4, md: 3 } }}
        >
            <Stack
                maxWidth="100%"
                width="100%"
                direction={{ xs: 'column', md: 'row' }}
                gap={{ xs: 6, md: 8, lg: 12 }}
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{ mx: 'auto', px: { xs: 2, sm: 4, lg: 8 } }}
            >
                <Stack width={{ xs: "100%", md: "38%" }} alignItems={{ xs: "center", md: "flex-start" }} gap={3}>
                    <Box sx={{ width: 140, height: 'auto' }}>
                        <Image src={logo} alt="Mood Global Services" style={{ width: '100%', height: 'auto', display: 'block' }} priority />
                    </Box>

                    <Stack gap={1} alignItems={{ xs: "center", md: "flex-start" }}>
                        <Typography variant="h5" fontWeight={500} textAlign={{ xs: "center", md: "left" }}>
                            Moods Dev Studio
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, lineHeight: 1.6 }} textAlign={{ xs: "center", md: "left" }}>
                            Your bespoke nerdy dev studio.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, lineHeight: 1.6 }} textAlign={{ xs: "center", md: "left" }}>
                            Build here, grow anywhere.
                        </Typography>
                    </Stack>

                    <Stack direction="row" gap={2} alignItems="center">
                        {SOCIAL_LINKS.map((social) => (
                            <Link key={social.id} href={social.href} target="_blank" rel="noopener noreferrer">
                                <Stack width={44} height={44} sx={{
                                    backgroundImage: `url(${social.grey.src})`,
                                    backgroundSize: "cover",
                                    transition: "background-image 0.3s ease-in-out, transform 0.3s ease-in-out",
                                    "&:hover": { backgroundImage: `url(${social.color.src})`, transform: "translateY(1px)" },
                                }} />
                            </Link>
                        ))}
                    </Stack>
                </Stack>

                <Stack direction="row" gap={{ md: 4, lg: 12 }} display={{ xs: "none", md: "flex" }} flexGrow={1} justifyContent="space-between">
                    {/* Navigate */}
                    <Stack gap={2} sx={{ minWidth: { md: 200, lg: 200 } }}>
                        <Typography variant="h6" fontWeight={300}>Navigate</Typography>
                        <Stack gap={2}>
                        {NAV_LINKS.map((link) => {
                            const isExternal = link.path.startsWith('http');
                            
                            return link.isSoon ? (
                                <Link key={link.label} href={link.path} style={{ textDecoration: 'none', cursor: 'default' }}>
                                    <Box sx={{ width: 'fit-content', color: theme.palette.text.secondary }}>
                                        <FooterRowWithBadge label={link.label} />
                                    </Box>
                                </Link>
                            ) : (
                                <Link 
                                    key={link.label} 
                                    href={link.path}
                                    target={isExternal ? "_blank" : "_self"}
                                    rel={isExternal ? "noopener noreferrer" : ""}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Typography variant="body2" sx={{ width: 'fit-content', color: theme.palette.text.secondary, transition: 'color 300ms ease', "&:hover": { color: 'text.primary', textDecoration: "underline" } }}>
                                        {link.label}
                                    </Typography>
                                </Link>
                            )
                        })}
                        </Stack>
                    </Stack>

                    {/* Legal */}
                    <Stack gap={2} sx={{ minWidth: { md: 200, lg: 200 } }}>
                        <Typography variant="h6" fontWeight={300}>Legal</Typography>
                        <Stack gap={2}>
                            {LEGAL_LINKS.map((link) => (
                                <Link key={link.label} href={link.path} style={{ textDecoration: 'none' }}>
                                    <Typography variant="body2" sx={{ width: 'fit-content', color: theme.palette.text.secondary, transition: 'color 300ms ease', "&:hover": { color: 'text.primary', textDecoration: "underline" } }}>
                                        {link.label}
                                    </Typography>
                                </Link>
                            ))}
                        </Stack>
                    </Stack>

                    {/* Contact */}
                    <Stack gap={2}>
                        <Typography variant="h6" fontWeight={300}>Contact</Typography>
                        <Stack gap={2} color="text.secondary">
                            <Link href={CONTACT_DATA.address.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" sx={{ width: 'fit-content', color: 'text.secondary', lineHeight: 1.6, transition: 'color 300ms ease', "&:hover": { color: 'text.primary', textDecoration: 'underline' } }}>
                                    {CONTACT_DATA.address.label.split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}
                                </Typography>
                            </Link>
                            <Link href={CONTACT_DATA.email.href} style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" sx={{ width: 'fit-content', color: 'text.secondary', transition: 'color 300ms ease', "&:hover": { color: 'text.primary', textDecoration: 'underline' } }}>
                                    {CONTACT_DATA.email.label}
                                </Typography>
                            </Link>
                            <Typography variant="body2" sx={{ width: 'fit-content', color: 'text.secondary', transition: 'color 300ms ease', "&:hover": { color: 'text.primary', textDecoration: 'none' } }}>
                                {CONTACT_DATA.kvk.label}
                            </Typography>
                            <Link href={CONTACT_DATA.tel.href} style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" sx={{ width: 'fit-content', color: 'text.secondary', transition: 'color 300ms ease', "&:hover": { color: 'text.primary', textDecoration: 'underline' } }}>
                                    {CONTACT_DATA.tel.label}
                                </Typography>
                            </Link>
                        </Stack>
                    </Stack>
                </Stack>

                {/* Mobile View Grid */}
                <Grid container spacing={2} sx={{ display: { xs: "flex", md: "none" }, width: "100%", overflow: 'hidden' }}>
                    <Grid size={{ xs: 6 }}>
                        <Stack width="100%" gap={2}>
                            <Typography variant="h6" fontWeight={300}>Navigate</Typography>
                            <Stack gap={2}>
                                {NAV_LINKS.map((link) => {
                                    const isExternal = link.path.startsWith('http');
                                    return link.isSoon ? (
                                        <Link key={link.label} href={link.path} style={{ textDecoration: 'none' }}><FooterRowWithBadge label={link.label} /></Link>
                                    ) : (
                                        <Link key={link.label} href={link.path} target={isExternal ? "_blank" : "_self"} rel={isExternal ? "noopener noreferrer" : ""} style={{ textDecoration: 'none' }}>
                                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{link.label}</Typography>
                                        </Link>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 6 }} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2 }}>
                        <Stack width="100%" gap={2}>
                            <Typography variant="h6" fontWeight={300}>Legal</Typography>
                            <Stack gap={2}>
                                {LEGAL_LINKS.map((link) => (
                                    <Link key={link.label} href={link.path} style={{ textDecoration: 'none' }}>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{link.label}</Typography>
                                    </Link>
                                ))}
                            </Stack>
                        </Stack>
                        <Stack width="100%" gap={2}>
                            <Typography variant="h6" fontWeight={300}>Contact</Typography>
                            <Stack gap={2} color="text.secondary">
                                <Link href={CONTACT_DATA.address.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{CONTACT_DATA.address.label}</Typography>
                                </Link>
                                <Link href={CONTACT_DATA.email.href} style={{ textDecoration: 'none' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{CONTACT_DATA.email.label}</Typography>
                                </Link>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{CONTACT_DATA.kvk.label}</Typography>
                                <Link href={CONTACT_DATA.tel.href} style={{ textDecoration: 'none' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{CONTACT_DATA.tel.label}</Typography>
                                </Link>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>

            {/* Bottom bar */}
            <Stack sx={{ mt: { xs: 6, md: 10 }, borderTop: `1px solid ${theme.palette.brand.border1.main}` }}>
                <Stack sx={{ maxWidth: "100%", px: { xs: 2, sm: 4, lg: 8 }, pt: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={300} color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, textAlign: { xs: 'center', md: 'left' } }}>
                        © {new Date().getFullYear()} Mood Global Services B.V. All rights reserved.
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
}

/* — Helpers — */

function FooterLink({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            variant="body2"
            fontWeight={300}
            color="text.secondary"
            sx={{ cursor: 'pointer', transition: 'color 160ms ease', '&:hover': { color: 'text.primary' } }}
        >
            {children}
        </Typography>
    );
}

function FooterRowWithBadge({ label }: { label: string }) {
    return (
        <Stack direction="row" alignItems="center" gap={1.25}>
            <FooterLink>{label}</FooterLink>
            <Badge>Coming Soon</Badge>
        </Stack>
    );
}

function Badge({ children }: { children: React.ReactNode }) {
    return (
        <Box sx={{ px: 1.25, py: 0.25, borderRadius: 1.25, border: `1px solid ${theme.palette.brand.border1.main}`, color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.6 }}>
            {children}
        </Box>
    );
}